export const diseasesModule = {
  id: "diseases",
  title: "Adrenal Gland: Diseases",
  sections: [
    {
      type: "text",
      content: `
        <h3>Overview of Adrenal Gland Diseases</h3>
        <p>Diseases of the adrenal gland can arise from either the <b>cortex</b> or the <b>medulla</b>, and can involve either <b>excess</b> or <b>deficiency</b> of hormones. Understanding which layer is affected and whether there is too much or too little hormone production is the key to distinguishing between the conditions.</p>
        <div class="highlight-box">
          <b>Cortex — excess hormone:</b>
          <ul>
            <li>Cushing's syndrome — excess cortisol (zona fasciculata)</li>
            <li>Conn's syndrome — excess aldosterone (zona glomerulosa)</li>
            <li>Congenital Adrenal Hyperplasia — excess androgens (zona reticularis)</li>
          </ul>
          <b>Cortex — deficiency:</b>
          <ul>
            <li>Addison's disease — deficiency of all three cortical hormones</li>
            <li>Waterhouse-Friderichsen syndrome — acute adrenal insufficiency from haemorrhage</li>
          </ul>
          <b>Medulla — excess hormone:</b>
          <ul>
            <li>Pheochromocytoma — excess catecholamines</li>
          </ul>
        </div>
        <p>This module covers all major adrenal diseases: Cushing's syndrome, Conn's syndrome, Addison's disease, Pheochromocytoma, Congenital Adrenal Hyperplasia (CAH), Waterhouse-Friderichsen syndrome, and adrenal incidentaloma.</p>
      `,
    },
    {
      type: "text",
      content: `
        <h3>Cushing's Syndrome</h3>
        <p><b class="purple-term">Cushing's syndrome</b> is the clinical syndrome caused by <b>prolonged excess of cortisol</b>. It is important to distinguish between the cause of the excess cortisol, as this determines the treatment.</p>

        <h3>Causes — ACTH-dependent vs ACTH-independent</h3>
        <p>The causes are divided into two groups depending on whether ACTH is driving the excess cortisol:</p>
        <ul>
          <li><b>ACTH-dependent</b> (ACTH is high — driving the adrenal glands to overproduce cortisol):
            <ul>
              <li><b class="purple-term">Cushing's disease</b> (most common endogenous cause) — a pituitary adenoma secreting excess ACTH, which drives bilateral adrenal hyperplasia and excess cortisol production. Note: "Cushing's disease" specifically refers to the pituitary cause; "Cushing's syndrome" is the broader term for excess cortisol from any cause.</li>
              <li><b>Ectopic ACTH secretion</b> — a non-pituitary tumour producing ACTH (e.g. small cell lung carcinoma, carcinoid tumours). Typically causes very high ACTH and rapid-onset features.</li>
            </ul>
          </li>
          <li><b>ACTH-independent</b> (the adrenal gland itself overproduces cortisol, suppressing ACTH):
            <ul>
              <li><b>Adrenal adenoma</b> — a benign tumour of the zona fasciculata producing cortisol autonomously</li>
              <li><b>Adrenal carcinoma</b> — rare but aggressive; may also produce androgens causing virilisation</li>
              <li><b>Exogenous steroids</b> (most common cause overall) — iatrogenic, from long-term corticosteroid therapy for other conditions</li>
            </ul>
          </li>
        </ul>
        <div class="highlight-box">
          <b>Most common cause of Cushing's syndrome overall:</b> Exogenous (iatrogenic) steroids<br/>
          <b>Most common endogenous cause:</b> Cushing's disease (pituitary ACTH-secreting adenoma)
        </div>
      `,
      image: "/images/adrenalglands/cushingscauses.png",
    },
    {
      type: "text",
      content: `
        <h3>Cushing's Syndrome — Clinical Features</h3>
        <p>The clinical features are all consequences of prolonged excess cortisol. Cortisol promotes gluconeogenesis, breaks down protein and fat, and has wide-reaching effects on multiple organ systems.</p>
        <ul>
          <li><b>Central obesity</b> with peripheral muscle wasting — cortisol promotes fat deposition centrally (trunk, abdomen) and breaks down peripheral muscle</li>
          <li><b>Moon face</b> (rounded, puffy face) and <b>buffalo hump</b> (fat deposition at the back of the neck)</li>
          <li><b>Purple striae</b> on the abdomen and flanks — cortisol breaks down collagen in the skin, causing wide, purple stretch marks (distinguish from normal silvery-white striae)</li>
          <li><b>Easy bruising</b> and <b>thin skin</b> — from impaired collagen synthesis</li>
          <li><b>Proximal myopathy</b> — difficulty rising from a chair or climbing stairs, from cortisol-induced muscle wasting</li>
          <li><b>Hypertension</b> — cortisol has weak mineralocorticoid activity (retains sodium and water)</li>
          <li><b>Hyperglycaemia / diabetes</b> — cortisol promotes gluconeogenesis and causes insulin resistance</li>
          <li><b>Osteoporosis</b> — cortisol inhibits osteoblasts and reduces calcium absorption</li>
          <li><b>Immunosuppression</b> — increased susceptibility to infections</li>
          <li><b>Hyperpigmentation</b> — only in ACTH-dependent causes (excess ACTH → excess MSH-like activity)</li>
          <li><b>Psychological effects</b> — depression, anxiety, psychosis</li>
          <li>In women: <b>oligomenorrhoea, hirsutism, acne</b> (from androgen excess, especially in adrenal carcinoma)</li>
        </ul>
        <div class="mnemonic-card">
          <div class="mnemonic-title">🎯 Memory Trick</div>
          <div class="mnemonic-content">
            <strong>"CUSHINGS"</strong>
            <ul>
              <li><b>C</b>entral obesity / Collagen loss (striae, bruising, thin skin)</li>
              <li><b>U</b>lcers (peptic ulcers from cortisol)</li>
              <li><b>S</b>kin changes (moon face, buffalo hump, striae)</li>
              <li><b>H</b>ypertension / Hyperglycaemia</li>
              <li><b>I</b>mmunosuppression</li>
              <li><b>N</b>ot strong (proximal myopathy)</li>
              <li><b>G</b>onadal dysfunction (oligomenorrhoea, impotence)</li>
              <li><b>S</b>tress fractures (osteoporosis)</li>
            </ul>
          </div>
        </div>
      `,
      image: "/images/adrenalglands/cushingsfeatures.png",
    },
    {
      type: "question",
      text: "What is the most common ENDOGENOUS cause of Cushing's syndrome?",
      options: [
        "Adrenal adenoma",
        "Ectopic ACTH from small cell lung carcinoma",
        "Pituitary ACTH-secreting adenoma (Cushing's disease)",
        "Adrenocortical carcinoma",
      ],
      correct: 2,
      explanation:
        "The most common endogenous cause of Cushing's syndrome is a pituitary ACTH-secreting adenoma, specifically called Cushing's disease. This accounts for approximately 70% of endogenous cases. Note that the most common cause of Cushing's syndrome overall (including exogenous causes) is long-term corticosteroid therapy.",
    },
    {
      type: "question",
      text: "A patient with Cushing's syndrome is found to have very high ACTH levels and a lung mass on CT. What is the most likely cause?",
      options: [
        "Cushing's disease (pituitary adenoma)",
        "Adrenal adenoma",
        "Ectopic ACTH secretion from a lung tumour",
        "Exogenous steroid use",
      ],
      correct: 2,
      explanation:
        "Very high ACTH levels combined with a lung mass points to ectopic ACTH secretion — most commonly from a small cell lung carcinoma or carcinoid tumour. In Cushing's disease (pituitary adenoma), ACTH is elevated but usually less dramatically so, and there is no lung mass. In adrenal adenoma or exogenous steroid use, ACTH is suppressed (low), not elevated.",
    },
    {
      type: "question",
      text: "Which of the following features is ONLY seen in ACTH-dependent causes of Cushing's syndrome (not in adrenal adenoma)?",
      options: [
        "Central obesity",
        "Proximal myopathy",
        "Hyperpigmentation",
        "Purple striae",
      ],
      correct: 2,
      explanation:
        "Hyperpigmentation is only seen in ACTH-dependent causes (Cushing's disease or ectopic ACTH), because excess ACTH shares a precursor molecule (POMC) with MSH, leading to increased skin pigmentation. In adrenal adenoma (ACTH-independent), ACTH is suppressed (low), so there is no hyperpigmentation. All other features are caused by excess cortisol itself and can occur in any cause.",
    },
    {
      type: "question",
      text: "A 38-year-old woman presents with central obesity, purple striae, easy bruising, and difficulty rising from a chair. Her blood pressure is 158/96 mmHg. What is the first-line screening test for Cushing's syndrome?",
      options: [
        "Random serum cortisol",
        "24-hour urine free cortisol or overnight low-dose dexamethasone suppression test",
        "ACTH stimulation test (Short Synacthen test)",
        "MRI pituitary",
      ],
      correct: 1,
      explanation:
        "The first-line screening tests for Cushing's syndrome are: (1) 24-hour urine free cortisol, (2) late-night salivary cortisol, or (3) overnight 1mg dexamethasone suppression test. In a normal person, dexamethasone suppresses cortisol production; in Cushing's syndrome, it fails to suppress. The ACTH stimulation test (Synacthen test) is used to diagnose Addison's disease, not Cushing's. MRI pituitary is only done once biochemical Cushing's is confirmed.",
    },
    {
      type: "text",
      content: `
        <h3>Cushing's Syndrome — Investigations</h3>
        <p>Investigations follow a two-step approach: first <b>confirm the diagnosis</b> (is cortisol truly elevated?), then <b>find the cause</b> (where is the excess coming from?).</p>

        <h3>Step 1: Confirm excess cortisol</h3>
        <ul>
          <li><b>Overnight 1mg dexamethasone suppression test</b> — give 1mg dexamethasone at midnight, measure serum cortisol at 9am. Normal: cortisol suppresses to &lt;50 nmol/L. In Cushing's: cortisol fails to suppress.</li>
          <li><b>24-hour urine free cortisol</b> — measures total cortisol production over 24 hours. Elevated in Cushing's.</li>
          <li><b>Late-night salivary cortisol</b> — cortisol normally falls to its lowest at midnight; in Cushing's it remains elevated.</li>
        </ul>

        <h3>Step 2: Find the cause (once diagnosis confirmed)</h3>
        <ul>
          <li><b>Serum ACTH level</b>:
            <ul>
              <li>ACTH <b>high</b> → ACTH-dependent cause (pituitary or ectopic)</li>
              <li>ACTH <b>low/suppressed</b> → ACTH-independent cause (adrenal adenoma/carcinoma)</li>
            </ul>
          </li>
          <li><b>High-dose dexamethasone suppression test</b> — in Cushing's disease (pituitary), high-dose dexamethasone partially suppresses cortisol; in ectopic ACTH production, it does not suppress.</li>
          <li><b>MRI pituitary</b> — to locate a pituitary adenoma</li>
          <li><b>CT chest/abdomen</b> — to look for ectopic ACTH source or adrenal tumour</li>
          <li><b>Inferior petrosal sinus sampling (IPSS)</b> — gold standard to distinguish pituitary from ectopic ACTH when imaging is inconclusive</li>
        </ul>

        <h3>Treatment</h3>
        <ul>
          <li><b>Cushing's disease:</b> Transsphenoidal surgery (removal of pituitary adenoma) is first-line</li>
          <li><b>Adrenal adenoma:</b> Adrenalectomy (laparoscopic)</li>
          <li><b>Ectopic ACTH:</b> Remove the source tumour if possible; medical therapy (metyrapone, ketoconazole) if not</li>
          <li><b>Exogenous:</b> Gradual tapering of steroid dose</li>
        </ul>
      `,
      image: "/images/adrenalglands/cushingsinvestigations.png",
    },
    {
      type: "question",
      text: "In a patient with confirmed Cushing's syndrome, the ACTH level is found to be undetectable (suppressed). What does this indicate?",
      options: [
        "Cushing's disease (pituitary adenoma)",
        "Ectopic ACTH production from a lung tumour",
        "Adrenal adenoma or adrenal carcinoma",
        "Addison's disease",
      ],
      correct: 2,
      explanation:
        "A suppressed (low/undetectable) ACTH in the context of Cushing's syndrome indicates an ACTH-independent cause — meaning the adrenal gland itself is autonomously overproducing cortisol. The excess cortisol feeds back to suppress pituitary ACTH secretion. This points to an adrenal adenoma (benign) or adrenal carcinoma (malignant). In pituitary or ectopic causes, ACTH is elevated.",
    },
    {
      type: "flashcard",
      front: "What is the difference between Cushing's syndrome and Cushing's disease?",
      back: `<ul>
        <li><b>Cushing's syndrome</b> = the clinical syndrome of excess cortisol from <em>any</em> cause (broad term)</li>
        <li><b>Cushing's disease</b> = Cushing's syndrome caused specifically by a <b>pituitary ACTH-secreting adenoma</b></li>
      </ul>
      <p>All Cushing's disease is Cushing's syndrome, but not all Cushing's syndrome is Cushing's disease.</p>`,
      hint: "One is the broad syndrome, one is a specific cause",
    },
    {
      type: "flashcard",
      front: "How do you distinguish between the causes of Cushing's syndrome using ACTH and dexamethasone suppression?",
      back: `<table style="width:100%; border-collapse: collapse; font-size: 0.85em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 6px; text-align:left">Cause</th>
          <th style="padding:4px 6px">ACTH</th>
          <th style="padding:4px 6px">Low-dose DST</th>
          <th style="padding:4px 6px">High-dose DST</th>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Cushing's disease</b> (pituitary)</td>
          <td style="padding:4px 6px; text-align:center">↑</td>
          <td style="padding:4px 6px; text-align:center">No suppression</td>
          <td style="padding:4px 6px; text-align:center">Partial suppression</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Ectopic ACTH</b></td>
          <td style="padding:4px 6px; text-align:center">↑↑ (very high)</td>
          <td style="padding:4px 6px; text-align:center">No suppression</td>
          <td style="padding:4px 6px; text-align:center">No suppression</td>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Adrenal adenoma</b></td>
          <td style="padding:4px 6px; text-align:center">↓ (suppressed)</td>
          <td style="padding:4px 6px; text-align:center">No suppression</td>
          <td style="padding:4px 6px; text-align:center">No suppression</td>
        </tr>
      </table>`,
      hint: "ACTH tells you dependent vs independent; high-dose DST separates pituitary from ectopic",
    },
    {
      type: "text",
      content: `
        <h3>Congenital Adrenal Hyperplasia (CAH)</h3>
        <p><b class="purple-term">Congenital adrenal hyperplasia (CAH)</b> is a group of autosomal recessive disorders caused by a deficiency of one of the enzymes needed to synthesise cortisol in the adrenal cortex. The most common form (>90% of cases) is <b class="purple-term">21-hydroxylase deficiency</b>.</p>

        <h3>Pathophysiology of 21-hydroxylase deficiency</h3>
        <p>The enzyme 21-hydroxylase is needed to convert cortisol precursors (17-hydroxyprogesterone) into cortisol and aldosterone. When it is deficient:
        <ol>
          <li>Cortisol production is <b>reduced</b> → the pituitary detects low cortisol and releases excess <b>ACTH</b></li>
          <li>Excess ACTH drives the adrenal gland to grow larger (<b>hyperplasia</b>) and work harder</li>
          <li>The precursors that cannot be converted to cortisol are instead shunted into the <b>androgen pathway</b> → <b>excess androgens</b> (testosterone, DHEA)</li>
          <li>Aldosterone production is also reduced → <b>salt-wasting</b> (in the classic form)</li>
        </ol>
        </p>
        <div class="highlight-box">
          <b>Key marker:</b> Elevated 17-hydroxyprogesterone (the blocked precursor) — this is the diagnostic test for 21-hydroxylase deficiency<br/>
          <b>Result:</b> ↓ Cortisol · ↓ Aldosterone (in classic form) · ↑ Androgens · ↑ ACTH · Adrenal hyperplasia
        </div>
      `,
      image: "/images/adrenalglands/CAHpathway.png",
    },
    {
      type: "text",
      content: `
        <h3>CAH — Clinical Presentations</h3>
        <p>There are three main clinical presentations of 21-hydroxylase deficiency depending on severity:</p>

        <h3>1. Classic salt-wasting CAH (most severe)</h3>
        <p>Presents in the <b>neonatal period</b> (typically within the first 2 weeks of life) with a life-threatening <b>adrenal crisis</b>:
        <ul>
          <li>Poor feeding, vomiting, failure to thrive</li>
          <li><b>Hyponatraemia</b> (salt wasting) and <b>hyperkalaemia</b></li>
          <li><b>Hypoglycaemia</b> (from cortisol deficiency)</li>
          <li>Shock and collapse</li>
          <li>In female neonates: <b>virilisation of external genitalia</b> (ambiguous genitalia / clitoromegaly) due to androgen excess in utero</li>
          <li>In male neonates: may appear normal at birth but develop precocious puberty</li>
        </ul>
        </p>

        <h3>2. Classic simple virilising CAH</h3>
        <p>Less severe — enough aldosterone is produced to avoid salt wasting, but androgen excess still causes:
        <ul>
          <li>Virilisation of female genitalia</li>
          <li>Accelerated growth and early pubic hair in childhood</li>
          <li>Early closure of growth plates → short final adult height</li>
        </ul>
        </p>

        <h3>3. Non-classic (late-onset) CAH</h3>
        <p>Mild enzyme deficiency presenting in adolescence or adulthood with:
        <ul>
          <li>Hirsutism, acne</li>
          <li>Menstrual irregularities</li>
          <li>Infertility</li>
          <li>May be mistaken for polycystic ovary syndrome (PCOS)</li>
        </ul>
        </p>

        <h3>Treatment</h3>
        <p>
        <ul>
          <li><b>Glucocorticoid replacement</b> (hydrocortisone) — replaces the deficient cortisol and suppresses excess ACTH (which in turn reduces androgen production)</li>
          <li><b>Mineralocorticoid replacement</b> (fludrocortisone) — for salt-wasting forms to replace aldosterone</li>
          <li><b>Salt supplementation</b> in infants</li>
          <li>Surgical correction of virilised genitalia in affected females</li>
          <li><b>Newborn screening</b> — 17-hydroxyprogesterone is measured on the heel prick test in many countries to detect CAH early</li>
        </ul>
        </p>
      `,
      image: "/images/adrenalglands/CAHclinical.png",
    },
    {
      type: "question",
      text: "What enzyme deficiency causes more than 90% of cases of congenital adrenal hyperplasia?",
      options: [
        "11β-hydroxylase",
        "17α-hydroxylase",
        "21-hydroxylase",
        "3β-hydroxysteroid dehydrogenase",
      ],
      correct: 2,
      explanation:
        "21-hydroxylase deficiency accounts for over 90% of all CAH cases. This enzyme is needed to convert cortisol precursors into cortisol and aldosterone. Its deficiency leads to cortisol and aldosterone deficiency, with excess androgens from the shunted precursors.",
    },
    {
      type: "question",
      text: "A 5-day-old female neonate presents with vomiting, poor feeding, and shock. Bloods show hyponatraemia, hyperkalaemia, and hypoglycaemia. Genitalia appear ambiguous. What is the most likely diagnosis?",
      options: [
        "Turner syndrome",
        "Classic salt-wasting congenital adrenal hyperplasia",
        "Addison's disease",
        "Pheochromocytoma",
      ],
      correct: 1,
      explanation:
        "This is a classic presentation of salt-wasting CAH in a neonate. The combination of adrenal crisis (shock, hypoglycaemia, hyponatraemia, hyperkalaemia) with virilised female genitalia (from in utero androgen excess) in the first days of life is highly characteristic. This is an endocrine emergency requiring immediate IV hydrocortisone and fluid resuscitation.",
    },
    {
      type: "question",
      text: "In 21-hydroxylase deficiency, what is the key diagnostic marker that accumulates due to the enzyme block?",
      options: [
        "Aldosterone",
        "Cortisol",
        "17-hydroxyprogesterone",
        "ACTH",
      ],
      correct: 2,
      explanation:
        "17-hydroxyprogesterone is the precursor that accumulates upstream of the blocked 21-hydroxylase enzyme. It is markedly elevated in 21-hydroxylase deficiency and is the key diagnostic marker — it is measured on the neonatal heel prick screening test and confirmed with a blood test when CAH is suspected.",
    },
    {
      type: "question",
      text: "Why does 21-hydroxylase deficiency lead to excess androgens?",
      options: [
        "The adrenal gland directly increases androgen synthesis in response to stress",
        "Cortisol precursors that cannot be converted to cortisol are shunted into the androgen synthesis pathway",
        "ACTH directly stimulates androgen production from the zona glomerulosa",
        "Aldosterone deficiency causes a compensatory increase in androgen production",
      ],
      correct: 1,
      explanation:
        "In 21-hydroxylase deficiency, the cortisol synthesis pathway is blocked. The precursors (17-hydroxyprogesterone) that build up cannot proceed to cortisol or aldosterone, so they are diverted ('shunted') into the androgen synthesis pathway, producing excess DHEA and androstenedione, which are converted to testosterone. This explains the virilisation seen in affected females.",
    },
    {
      type: "text",
      content: `
        <h3>Waterhouse-Friderichsen Syndrome</h3>
        <p><b class="purple-term">Waterhouse-Friderichsen syndrome</b> is <b>bilateral adrenal haemorrhage</b> leading to acute adrenal insufficiency, most classically caused by <b class="purple-term">Neisseria meningitidis</b> (meningococcal) septicaemia.</p>

        <h3>Pathophysiology</h3>
        <p>In severe sepsis (particularly meningococcal), the bacteria release endotoxins that trigger a massive inflammatory response and <b>disseminated intravascular coagulation (DIC)</b>. The adrenal glands are particularly susceptible to haemorrhage because they have a rich arterial blood supply but a limited venous drainage — this creates high pressure and makes the sinusoidal vessels prone to rupture when DIC disrupts normal clotting.</p>
        <p>The result is bilateral haemorrhagic infarction of the adrenal glands, producing <b>acute adrenal insufficiency</b> on top of an already critically unwell patient.</p>

        <h3>Clinical features</h3>
        <p>Typically presents in children or young adults with:
        <ul>
          <li>Signs of <b>meningococcal septicaemia</b>: high fever, rigors, rapidly spreading <b>petechial / purpuric rash</b> (non-blanching)</li>
          <li><b>Meningism</b> (neck stiffness, photophobia, headache) if meningitis is also present</li>
          <li>Rapidly developing <b>septic shock</b> — hypotension, tachycardia, cold peripheries</li>
          <li><b>Acute adrenal insufficiency</b> worsens the shock — profound hypotension refractory to fluids</li>
          <li>DIC — widespread purpura, bleeding from cannula sites</li>
        </ul>
        </p>
        <p>This is a <b>medical emergency</b> with a very high mortality rate. Any child presenting with a non-blanching petechial rash and fever should be treated as meningococcal septicaemia until proven otherwise.</p>

        <div class="highlight-box">
          <b>Classic scenario:</b> Child / young adult · Fever · Non-blanching purpuric rash · Rapid deterioration to shock · DIC<br/>
          <b>Cause:</b> Neisseria meningitidis septicaemia → bilateral adrenal haemorrhage<br/>
          <b>Emergency treatment:</b> IV/IM benzylpenicillin + IV hydrocortisone + aggressive fluid resuscitation · Transfer to ICU
        </div>

        <p><b>Other causes</b> of bilateral adrenal haemorrhage (less common): Pseudomonas septicaemia, anticoagulant therapy, trauma, post-operative stress.</p>
      `,
      image: "/images/adrenalglands/waterhouse.png",
    },
    {
      type: "question",
      text: "A 7-year-old child is brought to the emergency department with a 6-hour history of high fever, vomiting, and a rapidly spreading non-blanching purpuric rash. He is shocked with a blood pressure of 60/40 mmHg. What is the most likely underlying diagnosis and what must be given immediately?",
      options: [
        "Henoch-Schönlein purpura — oral steroids",
        "Meningococcal septicaemia with Waterhouse-Friderichsen syndrome — IV/IM benzylpenicillin and IV hydrocortisone",
        "Idiopathic thrombocytopenic purpura — platelet transfusion",
        "Viral meningitis — supportive care",
      ],
      correct: 1,
      explanation:
        "A non-blanching purpuric rash with fever and shock in a child is meningococcal septicaemia until proven otherwise — Waterhouse-Friderichsen syndrome (bilateral adrenal haemorrhage causing acute adrenal insufficiency) must be suspected. Treatment must not be delayed: IV or IM benzylpenicillin should be given immediately, along with IV hydrocortisone for the adrenal crisis and aggressive IV fluid resuscitation. This is one of the true paediatric emergencies.",
    },
    {
      type: "question",
      text: "Why are the adrenal glands particularly susceptible to haemorrhagic infarction in meningococcal septicaemia?",
      options: [
        "They have a poor blood supply and therefore cannot withstand any reduction in perfusion",
        "They have a rich arterial supply but limited venous drainage, making sinusoidal vessels prone to haemorrhage when DIC disrupts clotting",
        "They are particularly sensitive to bacterial endotoxins which directly destroy adrenal tissue",
        "They are located close to the inferior vena cava, which thromboses in DIC",
      ],
      correct: 1,
      explanation:
        "The adrenal glands have an unusually rich arterial blood supply (three arteries) but drain via a single central vein. This creates high pressure in the sinusoidal capillaries. When DIC (disseminated intravascular coagulation) occurs in severe sepsis, this high-pressure vascular bed is particularly prone to haemorrhagic infarction, leading to bilateral adrenal destruction.",
    },
    {
      type: "text",
      content: `
        <h3>Adrenal Incidentaloma</h3>
        <p>An <b class="purple-term">adrenal incidentaloma</b> is an adrenal mass discovered incidentally on imaging performed for an unrelated reason (e.g. CT abdomen for suspected appendicitis, or for staging a cancer). With the widespread use of CT scanning, they are increasingly common — found in approximately <b>4–7% of CT scans</b> in adults, rising to >10% in the elderly.</p>

        <h3>The two key questions when an adrenal incidentaloma is found</h3>
        <ol>
          <li><b>Is it hormonally active?</b> (Is it secreting excess hormones?) — needs biochemical workup</li>
          <li><b>Is it malignant?</b> — needs radiological assessment and possibly biopsy/surgery</li>
        </ol>

        <h3>Biochemical workup (to exclude hormone excess)</h3>
        <ul>
          <li><b>Overnight dexamethasone suppression test</b> — to exclude subclinical Cushing's syndrome</li>
          <li><b>24-hour urine metanephrines or plasma metanephrines</b> — to exclude pheochromocytoma (MUST be excluded before any biopsy or surgery, as manipulation of an unrecognised phaeochromocytoma can cause a fatal hypertensive crisis)</li>
          <li><b>Aldosterone:renin ratio</b> — if the patient is hypertensive, to exclude Conn's syndrome</li>
          <li>In appropriate clinical context: DHEA-S, testosterone (if adrenal carcinoma suspected)</li>
        </ul>

        <h3>Radiological features suggesting malignancy</h3>
        <ul>
          <li><b>Size &gt;4 cm</b> — risk of malignancy increases significantly above this threshold</li>
          <li><b>Irregular margins</b> or heterogeneous appearance</li>
          <li><b>High density on non-contrast CT</b> (HU &gt;10) — lipid-poor lesions are more suspicious; benign adenomas are typically lipid-rich (&lt;10 HU)</li>
          <li><b>Slow washout of contrast</b> on delayed CT — adenomas wash out contrast quickly; malignant lesions retain it</li>
          <li>History of known primary malignancy — adrenal glands are a common site of metastases (lung, breast, renal cell carcinoma, melanoma)</li>
        </ul>

        <div class="highlight-box">
          <b>Management summary:</b><br/>
          All incidentalomas: biochemical testing (cortisol, metanephrines, aldosterone:renin)<br/>
          &gt;4 cm or suspicious imaging: adrenalectomy<br/>
          &lt;4 cm, non-functioning, benign features: surveillance imaging at 6–12 months
        </div>
      `,
      image: "/images/adrenalglands/incidentaloma.png",
    },
    {
      type: "question",
      text: "A 55-year-old man has a 3.5 cm right adrenal mass found on CT abdomen performed for abdominal pain. He has no hypertension or symptoms of hormone excess. Which investigation must be performed BEFORE any surgical intervention or biopsy?",
      options: [
        "Aldosterone:renin ratio",
        "Low-dose dexamethasone suppression test",
        "24-hour urine or plasma metanephrines",
        "Serum ACTH level",
      ],
      correct: 2,
      explanation:
        "Pheochromocytoma must always be excluded first before any biopsy or surgery on an adrenal mass. Surgical manipulation or biopsy of an unrecognised phaeochromocytoma can trigger a massive catecholamine release, causing a life-threatening hypertensive crisis. 24-hour urine or plasma metanephrines are the test of choice. Once phaeochromocytoma is excluded, other hormone excess (cortisol, aldosterone) can be assessed.",
    },
    {
      type: "question",
      text: "On non-contrast CT, an adrenal incidentaloma has a density of 5 Hounsfield Units (HU). What does this suggest?",
      options: [
        "Likely malignant — needs urgent surgery",
        "Likely a lipid-rich benign adrenal adenoma",
        "Likely a pheochromocytoma",
        "Likely an adrenal metastasis",
      ],
      correct: 1,
      explanation:
        "A density of ≤10 HU on non-contrast CT indicates a lipid-rich lesion, which is characteristic of a benign adrenal adenoma. Adrenal adenomas store cholesterol (for steroid hormone synthesis) and appear low density on CT. A density >10 HU is lipid-poor and more suspicious for malignancy or other pathology. This CT density threshold (10 HU) is one of the most useful features in evaluating adrenal incidentalomas.",
    },
    {
      type: "flashcard",
      front: "What are the key adrenal gland diseases and which hormone/layer is involved?",
      back: `<table style="width:100%; border-collapse:collapse; font-size:0.85em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 6px; text-align:left">Disease</th>
          <th style="padding:4px 6px">Layer</th>
          <th style="padding:4px 6px">Hormone change</th>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Cushing's syndrome</b></td>
          <td style="padding:4px 6px; text-align:center">Zona fasciculata</td>
          <td style="padding:4px 6px; text-align:center">↑ Cortisol</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Conn's syndrome</b></td>
          <td style="padding:4px 6px; text-align:center">Zona glomerulosa</td>
          <td style="padding:4px 6px; text-align:center">↑ Aldosterone</td>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>CAH (21-OH def)</b></td>
          <td style="padding:4px 6px; text-align:center">All cortex</td>
          <td style="padding:4px 6px; text-align:center">↑ Androgens, ↓ Cortisol, ↓ Aldosterone</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>Addison's disease</b></td>
          <td style="padding:4px 6px; text-align:center">All cortex (destroyed)</td>
          <td style="padding:4px 6px; text-align:center">↓ All cortical hormones</td>
        </tr>
        <tr>
          <td style="padding:4px 6px"><b>Phaeochromocytoma</b></td>
          <td style="padding:4px 6px; text-align:center">Medulla</td>
          <td style="padding:4px 6px; text-align:center">↑ Catecholamines</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 6px"><b>WF syndrome</b></td>
          <td style="padding:4px 6px; text-align:center">All (haemorrhage)</td>
          <td style="padding:4px 6px; text-align:center">↓ All hormones (acute)</td>
        </tr>
      </table>`,
      hint: "Map each disease to its layer and hormone direction",
    },
    {
      type: "text",
      content: `
        <h3>Pheochromocytoma</h3>
        <p>A <b class="purple-term">pheochromocytoma</b> is a tumour of the <b>chromaffin cells of the adrenal medulla</b> that secretes excess catecholamines (adrenaline and noradrenaline). It follows the "rule of 10s":
        <ul>
          <li>10% are <b>bilateral</b></li>
          <li>10% are <b>extra-adrenal</b> (called paragangliomas)</li>
          <li>10% are <b>malignant</b></li>
          <li>10% occur in <b>children</b></li>
          <li>10% are <b>familial</b> (associated with MEN2, VHL, NF1)</li>
        </ul>
        </p>
        <p><b>Clinical features</b> are caused by excess catecholamines and classically present as episodic (paroxysmal):
        <ul>
          <li><b>Hypertension</b> (often paroxysmal — sudden severe spikes)</li>
          <li><b>Headache</b></li>
          <li><b>Sweating</b></li>
          <li><b>Palpitations</b></li>
          <li><b>Pallor</b></li>
        </ul>
        </p>
        <p><b>Diagnosis:</b> 24-hour urine or plasma metanephrines (breakdown products of catecholamines). CT or MRI for localization.<br/>
        <b>Treatment:</b> Surgical removal (adrenalectomy). Before surgery, alpha-blockers must be given first (e.g. phenoxybenzamine) to prevent dangerous hypertensive crises during tumour manipulation — only then are beta-blockers added.</p>
      `,
      image: "/images/adrenalglands/phaeochromocytoma.png",
    },
    {
      type: "question",
      text: "A pheochromocytoma arises from which cell type?",
      options: [
        "Zona glomerulosa cells",
        "Zona fasciculata cells",
        "Chromaffin cells of the adrenal medulla",
        "Zona reticularis cells",
      ],
      correct: 2,
      explanation:
        "A pheochromocytoma arises from the chromaffin cells of the adrenal medulla, which are modified postganglionic sympathetic neurons that secrete catecholamines (adrenaline and noradrenaline) directly into the bloodstream.",
    },
    {
      type: "question",
      text: "A 42-year-old woman presents with episodic severe headache, sweating, palpitations and hypertensive crises. A pheochromocytoma is suspected. Which investigation should be ordered first?",
      options: [
        "CT abdomen",
        "24-hour urine metanephrines",
        "Serum cortisol",
        "Renin:aldosterone ratio",
      ],
      correct: 1,
      explanation:
        "The first investigation for suspected pheochromocytoma is biochemical confirmation — 24-hour urine metanephrines (or plasma metanephrines). CT or MRI for localization is only done after biochemical confirmation to avoid missing extra-adrenal tumours (paragangliomas).",
    },
    {
      type: "question",
      text: "Before surgery for a pheochromocytoma, which drug class must be given FIRST to prevent a hypertensive crisis during tumour handling?",
      options: [
        "Beta-blockers",
        "ACE inhibitors",
        "Alpha-blockers",
        "Calcium channel blockers",
      ],
      correct: 2,
      explanation:
        "Alpha-blockers (e.g. phenoxybenzamine) must be given first to block the effects of the excess catecholamines and control blood pressure. If beta-blockers are given first without alpha-blockade, unopposed alpha-adrenergic stimulation can cause severe vasoconstriction and a life-threatening hypertensive crisis.",
    },
    {
      type: "flashcard",
      front: "What are the five classic symptoms of a pheochromocytoma?",
      back: `<ul>
        <li><b>H</b>ypertension (paroxysmal)</li>
        <li><b>H</b>eadache</li>
        <li><b>H</b>yperhidrosis (sweating)</li>
        <li><b>H</b>eart palpitations</li>
        <li><b>P</b>allor</li>
      </ul>
      <p>Remember: <b>5 H's</b> (some use 4 H's + Pallor)</p>`,
      hint: "Think of the fight-or-flight response on overdrive",
    },
    {
      type: "flashcard",
      front: "What is the 'rule of 10s' for pheochromocytoma?",
      back: `<ul>
        <li>10% <b>bilateral</b></li>
        <li>10% <b>extra-adrenal</b> (paraganglioma)</li>
        <li>10% <b>malignant</b></li>
        <li>10% in <b>children</b></li>
        <li>10% <b>familial</b> (MEN2, VHL, NF1)</li>
      </ul>`,
      hint: "Everything about phaeochromocytoma comes in 10s",
    },
    {
      type: "text",
      content: `
        <h3>Conn's Syndrome (Primary Hyperaldosteronism)</h3>
        <p><b class="purple-term">Conn's syndrome</b> is caused by excess production of <b>aldosterone</b> from the <b>zona glomerulosa</b> of the adrenal cortex, most commonly due to a <b>benign adrenal adenoma</b> (Conn's adenoma). It is the most common cause of secondary hypertension.</p>
        <p><b>Clinical features</b> result from the effects of excess aldosterone on the kidneys:
        <ul>
          <li><b>Hypertension</b> (often resistant to medication)</li>
          <li><b>Hypokalaemia</b> (low potassium — aldosterone promotes potassium excretion)</li>
          <li>Muscle weakness and cramps (from hypokalaemia)</li>
          <li>Polyuria and polydipsia (from hypokalaemia-induced nephrogenic diabetes insipidus)</li>
          <li>Metabolic alkalosis (sodium/water retention + potassium/H+ loss)</li>
        </ul>
        </p>
        <div class="highlight-box">
          <b>Key labs:</b> Hypertension + Hypokalaemia + Metabolic alkalosis + Low renin<br/>
          <b>Screening test:</b> Aldosterone:renin ratio (elevated in Conn's)<br/>
          <b>Treatment:</b> Adrenalectomy (for adenoma) or mineralocorticoid receptor antagonists e.g. spironolactone (for bilateral hyperplasia)
        </div>
      `,
      image: "/images/adrenalglands/connssyndrome.png",
    },
    {
      type: "question",
      text: "Which electrolyte abnormality is most characteristic of Conn's syndrome?",
      options: [
        "Hyperkalaemia",
        "Hypokalaemia",
        "Hypernatraemia",
        "Hyponatraemia",
      ],
      correct: 1,
      explanation:
        "Conn's syndrome (primary hyperaldosteronism) is characterised by hypokalaemia (low potassium), because excess aldosterone drives potassium excretion in the distal tubule and collecting duct of the kidney. It also causes sodium retention, hypertension, and metabolic alkalosis.",
    },
    {
      type: "question",
      text: "In Conn's syndrome, what would you expect to find on the aldosterone:renin ratio?",
      options: [
        "Low aldosterone, high renin",
        "High aldosterone, high renin",
        "High aldosterone, low renin",
        "Low aldosterone, low renin",
      ],
      correct: 2,
      explanation:
        "In Conn's syndrome (primary hyperaldosteronism), aldosterone is autonomously overproduced by the adrenal gland. The high aldosterone causes sodium retention and volume expansion, which suppresses renin release. This gives a characteristic pattern of high aldosterone with low renin — an elevated aldosterone:renin ratio is the key screening test.",
    },
    {
      type: "text",
      content: `
        <h3>Addison's Disease (Primary Adrenal Insufficiency)</h3>
        <p><b class="purple-term">Addison's disease</b> is caused by <b>destruction of the adrenal cortex</b>, leading to deficiency of all three cortical hormones: cortisol, aldosterone, and androgens. In the developed world, the most common cause is <b>autoimmune adrenalitis</b> (autoimmune destruction of the cortex). Other causes include tuberculosis, metastatic cancer, and haemorrhage (Waterhouse-Friderichsen syndrome in meningococcal sepsis).</p>
        <p><b>Clinical features</b>:
        <ul>
          <li><b>Fatigue and weakness</b></li>
          <li><b>Weight loss and anorexia</b></li>
          <li><b>Hypotension</b> (especially postural) — from aldosterone deficiency</li>
          <li><b>Hyperpigmentation</b> of skin and mucous membranes — the pituitary produces excess ACTH (trying to stimulate the failing adrenal cortex), and ACTH shares a precursor with MSH (melanocyte-stimulating hormone), causing darkening of the skin. This is a distinguishing feature of PRIMARY adrenal failure.</li>
          <li><b>Hypoglycaemia</b> — from cortisol deficiency</li>
          <li><b>Hyperkalaemia and hyponatraemia</b> — from aldosterone deficiency</li>
          <li>Salt craving</li>
        </ul>
        </p>
        <p>An <b class="purple-term">Addisonian crisis</b> is a life-threatening acute adrenal insufficiency, typically triggered by physiological stress (infection, surgery, trauma) in a patient with chronic insufficiency. It presents with severe hypotension, vomiting, confusion, and can be fatal. Treatment is immediate IV hydrocortisone and IV fluids.</p>
        <div class="highlight-box">
          <b>Key labs:</b> Hyponatraemia + Hyperkalaemia + Hypoglycaemia + Elevated ACTH + Low cortisol<br/>
          <b>Confirmatory test:</b> Short Synacthen test (ACTH stimulation test) — cortisol fails to rise adequately<br/>
          <b>Treatment:</b> Lifelong hydrocortisone (cortisol replacement) + fludrocortisone (aldosterone replacement)
        </div>
      `,
      image: "/images/adrenalglands/addisonsdisease.png",
    },
    {
      type: "question",
      text: "A patient with Addison's disease has marked hyperpigmentation of the skin and buccal mucosa. What is the mechanism?",
      options: [
        "Excess cortisol stimulating melanocytes",
        "Excess ACTH (which shares a precursor with MSH) stimulating melanocytes",
        "Aldosterone causing pigment deposition",
        "Autoimmune destruction of melanocytes",
      ],
      correct: 1,
      explanation:
        "In primary adrenal insufficiency (Addison's disease), the failing adrenal cortex cannot produce cortisol, so the pituitary gland increases ACTH secretion in an attempt to stimulate the cortex. ACTH is cleaved from the same precursor molecule (POMC) as melanocyte-stimulating hormone (MSH), so excess ACTH production leads to increased MSH activity and hyperpigmentation. This hyperpigmentation is a feature of PRIMARY adrenal failure only — in secondary adrenal failure (pituitary problem), ACTH is low.",
    },
    {
      type: "question",
      text: "Which electrolyte pattern is typical of Addison's disease?",
      options: [
        "Hypernatraemia and hypokalaemia",
        "Hyponatraemia and hyperkalaemia",
        "Hypernatraemia and hyperkalaemia",
        "Hyponatraemia and hypokalaemia",
      ],
      correct: 1,
      explanation:
        "Addison's disease causes aldosterone deficiency, which means the kidneys cannot retain sodium or excrete potassium properly. This results in hyponatraemia (low sodium) and hyperkalaemia (high potassium). This is the opposite pattern to Conn's syndrome (high aldosterone), which causes hypokalaemia and sodium retention.",
    },
    {
      type: "question",
      text: "What is the confirmatory test for Addison's disease?",
      options: [
        "24-hour urine cortisol",
        "Short Synacthen test (ACTH stimulation test)",
        "Low-dose dexamethasone suppression test",
        "Aldosterone:renin ratio",
      ],
      correct: 1,
      explanation:
        "The Short Synacthen test (SST) is the confirmatory test for Addison's disease. Synthetic ACTH (synacthen) is given and serum cortisol is measured 30–60 minutes later. In a healthy person, cortisol rises significantly. In Addison's disease, the destroyed adrenal cortex cannot respond to ACTH, so cortisol fails to rise — confirming primary adrenal insufficiency.",
    },
    {
      type: "flashcard",
      front: "What are the differences between Conn's syndrome and Addison's disease?",
      back: `<table style="width:100%; border-collapse: collapse; font-size: 0.9em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 8px; text-align:left">Feature</th>
          <th style="padding:4px 8px">Conn's</th>
          <th style="padding:4px 8px">Addison's</th>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Aldosterone</b></td>
          <td style="padding:4px 8px; text-align:center">↑</td>
          <td style="padding:4px 8px; text-align:center">↓</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 8px"><b>Blood pressure</b></td>
          <td style="padding:4px 8px; text-align:center">↑</td>
          <td style="padding:4px 8px; text-align:center">↓</td>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Potassium</b></td>
          <td style="padding:4px 8px; text-align:center">↓ (hypo)</td>
          <td style="padding:4px 8px; text-align:center">↑ (hyper)</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 8px"><b>Sodium</b></td>
          <td style="padding:4px 8px; text-align:center">↑ (retained)</td>
          <td style="padding:4px 8px; text-align:center">↓ (lost)</td>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Renin</b></td>
          <td style="padding:4px 8px; text-align:center">↓ (suppressed)</td>
          <td style="padding:4px 8px; text-align:center">↑</td>
        </tr>
      </table>`,
      hint: "Conn's = too much aldosterone. Addison's = too little of everything.",
    },
  ],
};
